import { useCallback, useEffect, useRef, useState } from 'react';
import LoadFilterSimple from '../../../../../common/components/load-filters/load-filter-simple/load-filter-simple';
import { useOffersRound } from '../../../../../app/contexts/offers-round/offers-round.context';
import LoadFilterAdvanced from '../../../../../common/components/load-filters/load-filter-advanced/load-filter-advanced';
import {
    getMaterialResultOptions,
    getOfferStatusByRoundPhase,
    getUFResultOptions,
    mapDescriptionToSelectInputModel,
} from './offer-filters.utils';
import ISelectInputModel from '../../../../../common/components/load-filters/load-filter-advanced/components/location-filter/interfaces/ISelectInputModel';
import RoundManagerApiService from '../../../../../common/services/roundmanager-api';
import ILoadMaterialResponse from '../../../../../common/components/load-filters/interfaces/ILoadMaterialResponse';
import { getLoadChannelOptions } from '../../../../../common/enums/loads/load-channel-types.enum';
import LoadsFilterLocationType from '../../../../../common/enums/loads/loads-filter-tags-location-type.enum';
import ILoadsLocationResponse from '../../../../../common/components/load-filters/interfaces/ILoadsLocationResponse';
import ILoadsFiltersModel from '../../../../../common/interfaces/IFiltersModel';
import ILoadsLocationModel from '../../../../../common/interfaces/loads/ILoadsLocationModel';
import LoadFilterTags from '../../../../../common/components/load-filters/load-filter-tags/load-filter-tags';
import {
    setChannelFilterProps,
    setDateFilterProps,
    setDestinationLocationFilterProps,
    setMaterialFilterProps,
    setSourceLocationFilterProps,
} from '../../../../../common/utils/load-offers-utils';

export default function OfferFilter() {
    const { filters, roundState, dispatch } = useOffersRound();

    const fetchInitialData = useRef(true);

    const [openAdvancedFilter, setOpenAdvancedFilter] =
        useState<boolean>(false);
    const [advancedFilter, setAdvancedFilter] = useState<
        Partial<ILoadsFiltersModel>
    >(filters.current);
    const advancedFilters = useRef<ILoadsFiltersModel>(
        {} as ILoadsFiltersModel,
    );
    const [materialOptions, setMaterialOptions] = useState<ISelectInputModel[]>(
        [] as ISelectInputModel[],
    );
    const [sourceUFOptions, setSourceUFOptions] = useState<ISelectInputModel[]>(
        [] as ISelectInputModel[],
    );
    const [sourceDescriptionOptions, setSourceDescriptionOptions] = useState<
        ISelectInputModel[]
    >([] as ISelectInputModel[]);

    const [destinationUFOptions, setDestinationUFOptions] = useState<
        ISelectInputModel[]
    >([] as ISelectInputModel[]);
    const [destinationDescriptionOptions, setDestinationDescriptionOptions] =
        useState<ISelectInputModel[]>([] as ISelectInputModel[]);

    const setAdvancedFilters = useCallback(
        (filtersModel: ILoadsFiltersModel) => {
            advancedFilters.current = filtersModel;
            setAdvancedFilter(filtersModel);
        },
        [setAdvancedFilter],
    );

    const onApplyFilters = useCallback(
        (filtersModel: ILoadsFiltersModel) => {
            filters.current = filtersModel;
            dispatch({ type: 'FILTERING_OFFERS' });
        },
        [filters, dispatch],
    );

    const setFilters = useCallback(
        (filtersModel: ILoadsFiltersModel) => {
            setAdvancedFilter(filtersModel);
            onApplyFilters(filtersModel);
        },
        [onApplyFilters],
    );

    const closeAdvancedFilterDrawer = useCallback(() => {
        advancedFilters.current = {};
        setOpenAdvancedFilter(false);
        setAdvancedFilter(filters.current);
    }, [advancedFilters, filters]);

    const cleanCurrentFilters = useCallback(() => {
        onApplyFilters({});
        closeAdvancedFilterDrawer();
    }, [closeAdvancedFilterDrawer, onApplyFilters]);

    const onApplyAdvancedFilters = useCallback(() => {
        filters.current = advancedFilters.current;
        closeAdvancedFilterDrawer();
        dispatch({ type: 'FILTERING_OFFERS' });
    }, [filters, closeAdvancedFilterDrawer, dispatch]);

    const handleMaterialSearch = useCallback(async (value: string) => {
        const { response } = await RoundManagerApiService.Offers.GetMaterials(
            value,
            1,
            10,
        );

        if (response) {
            return response.data;
        }

        return [] as ILoadMaterialResponse[];
    }, []);

    const handleMaterialSearchResult = useCallback(
        (data: ILoadMaterialResponse[]) => {
            setMaterialOptions(getMaterialResultOptions(data));
        },
        [setMaterialOptions],
    );

    const handleSourceDescriptionSearchResult = useCallback(
        (data: ILoadsLocationResponse[]) => {
            const sourceDescriptions = mapDescriptionToSelectInputModel(data);
            setSourceDescriptionOptions(sourceDescriptions);
        },
        [],
    );

    const handleDestinationDescriptionSearchResult = useCallback(
        (data: ILoadsLocationResponse[]) => {
            const destOptions = mapDescriptionToSelectInputModel(data);
            setDestinationDescriptionOptions(destOptions);
        },
        [],
    );

    const fetchMaterialsFilterValues = useCallback(async () => {
        const results = await RoundManagerApiService.Offers.GetMaterials(
            undefined,
            1,
            10,
            false,
        );

        handleMaterialSearchResult([...(results.response?.data ?? [])]);
    }, [handleMaterialSearchResult]);

    const getLoadsLocation = useCallback(
        async (filter: ILoadsLocationModel) => {
            return RoundManagerApiService.Loads.getLoadsLocation(filter);
        },
        [],
    );

    const fetchLocationsFilterValues = useCallback(
        async (
            locationType: LoadsFilterLocationType,
            setUFOptions: (options: ISelectInputModel[]) => void,
            handleDescriptionSearchResult: (
                data: ILoadsLocationResponse[],
            ) => void,
        ) => {
            const locations = await getLoadsLocation({
                locationType,
            } as ILoadsLocationModel);

            if (locations.response) {
                const { data } = locations.response;
                const states = data.map(location => location.federativeUnit);

                setUFOptions([...getUFResultOptions(states ?? [])]);
                handleDescriptionSearchResult([...(data ?? [])]);
            }
        },
        [getLoadsLocation],
    );

    const handleSourceDescriptionSearch = useCallback(
        async (value: string) => {
            const data = await getLoadsLocation({
                locationType: LoadsFilterLocationType.Source,
                search: value,
                _page: 1,
                _size: 10,
            });
            return data.response?.data ?? [];
        },
        [getLoadsLocation],
    );

    const handleDestinationDescriptionSearch = useCallback(
        async (value: string) => {
            const data = await getLoadsLocation({
                locationType: LoadsFilterLocationType.Destination,
                search: value,
                _page: 1,
                _size: 10,
            });
            return data.response?.data ?? [];
        },
        [getLoadsLocation],
    );

    const setAllFilters = useCallback(async () => {
        setTimeout(() => {
            fetchLocationsFilterValues(
                LoadsFilterLocationType.Source,
                setSourceUFOptions,
                handleSourceDescriptionSearchResult,
            );

            fetchLocationsFilterValues(
                LoadsFilterLocationType.Destination,
                setDestinationUFOptions,
                handleDestinationDescriptionSearchResult,
            );

            fetchMaterialsFilterValues();

            fetchInitialData.current = false;
        }, 1000);
    }, [
        fetchLocationsFilterValues,
        fetchMaterialsFilterValues,
        handleDestinationDescriptionSearchResult,
        handleSourceDescriptionSearchResult,
    ]);

    useEffect(() => {
        if (fetchInitialData.current && roundState.status === 'loadingOffers') {
            setAllFilters();
        }

        if (roundState.status === 'loadingPhase') {
            fetchInitialData.current = true;
        }
    }, [setAllFilters, roundState.status]);

    return (
        <div>
            <div className="offer-filters-container">
                <LoadFilterSimple
                    filters={filters}
                    setOpenAdvancedFilter={() => setOpenAdvancedFilter(true)}
                    setFilters={setFilters}
                    getLoadStatusOptions={() =>
                        getOfferStatusByRoundPhase(roundState)
                    }
                    getLoadChannelOptions={getLoadChannelOptions}
                    hideLoadField={roundState.currentPhase !== 'Confirmation'}
                />
                <LoadFilterAdvanced
                    open={openAdvancedFilter}
                    onClose={closeAdvancedFilterDrawer}
                    onApply={onApplyAdvancedFilters}
                    onClear={cleanCurrentFilters}
                    dateFilterProps={setDateFilterProps(
                        advancedFilter,
                        setAdvancedFilters,
                        new Date(),
                    )()}
                    channelFilterProps={setChannelFilterProps(
                        advancedFilter,
                        setAdvancedFilters,
                        getLoadChannelOptions,
                    )()}
                    materialFilterProps={setMaterialFilterProps(
                        advancedFilter,
                        setAdvancedFilters,
                        handleMaterialSearch,
                        handleMaterialSearchResult,
                        materialOptions,
                    )()}
                    sourceLocationFilterProps={setSourceLocationFilterProps(
                        advancedFilter,
                        sourceUFOptions,
                        setAdvancedFilters,
                        sourceDescriptionOptions,
                        handleSourceDescriptionSearch,
                        handleSourceDescriptionSearchResult,
                    )()}
                    destinationLocationFilterProps={setDestinationLocationFilterProps(
                        advancedFilter,
                        destinationUFOptions,
                        setAdvancedFilters,
                        destinationDescriptionOptions,
                        handleDestinationDescriptionSearch,
                        handleDestinationDescriptionSearchResult,
                    )()}
                />
            </div>

            <LoadFilterTags
                filters={filters}
                materialOptions={materialOptions}
                cleanCurrentFilters={cleanCurrentFilters}
                onApplyFilters={onApplyFilters}
            />
        </div>
    );
}
